import React from 'react';
import { Clock, CheckCircle, XCircle, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import MesCommandesMaquetteBlock, {
  MesCommandesMaquetteEmptyRow,
} from './MesCommandesMaquetteBlock';
import {
  MesCommandesProductReferenceRow,
  MesCommandesRdvReferenceRow,
  MesCommandesProductTableHead,
  MesCommandesRdvTableHead,
} from './MesCommandesReferenceRows';
import { MES_COMMANDES_MAQUETTE_TABLE_PLATFORM_CLASS } from './mesCommandesMaquetteTableLayout';
import { MES_COMMANDES_PRESTATIONS_RDV_BLOCK_TITLE } from '../../utils/mesCommandesMixedUx';
import { buildMesCommandesRowContext } from '../../utils/mesCommandesRowContextBuilder';

const NEW_COMMANDE_ROW_CLASS =
  'border-l-4 shadow-md hover:bg-blue-50/40 cursor-pointer transition-all duration-300';

const NEW_COMMANDE_ROW_STYLE = {
  background: 'linear-gradient(90deg, rgba(74, 20, 140, 0.1) 0%, rgba(233, 30, 99, 0.1) 100%)',
  borderLeftColor: '#4A148C',
  animation: 'flash 2s ease-in-out infinite',
};

function renderPlatformRow(commande, options) {
  const {
    ctx,
    rowHandlers,
    dimmed = false,
    isNewCommande = false,
  } = options;
  const rowClassName = isNewCommande ? NEW_COMMANDE_ROW_CLASS : undefined;
  const rowStyle = isNewCommande ? NEW_COMMANDE_ROW_STYLE : undefined;
  const common = {
    key: commande.id,
    commande,
    ctx,
    dimmed,
    showCommercantColumn: true,
    rowClassName,
    rowStyle,
    ...rowHandlers,
  };
  if (ctx.isRdv) {
    return <MesCommandesRdvReferenceRow {...common} />;
  }
  return <MesCommandesProductReferenceRow {...common} />;
}

/**
 * Listes maquette Mes commandes — vue Super Admin (colonne commerçant).
 */
export default function PlatformCommandesMaquetteLists({
  commandes,
  isRendezVous,
  isCommandeEnCours,
  sectionsExpanded,
  onToggleSection,
  newCommandes = [],
  rowContextDeps,
  rowHandlers,
  showProductSections = true,
  showRdvSections = true,
}) {
  const commandesEnCours = commandes.filter((cmd) => isCommandeEnCours(cmd.statut));
  const produitsEnCours = commandesEnCours.filter((cmd) => !isRendezVous(cmd));
  const rdvEnCours = commandesEnCours.filter((cmd) => isRendezVous(cmd));

  const commandesTerminees = commandes.filter((cmd) => {
    const statut = cmd.statut;
    return statut === 'LIVREE' || statut === 'RETIREE' || statut === 'TERMINE';
  });
  const commandesAnnulees = commandes.filter((cmd) => {
    const statut = cmd.statut;
    return statut === 'ANNULEE' || statut === 'ANNULE';
  });

  const termineesProduits = commandesTerminees.filter((c) => !isRendezVous(c));
  const termineesRdv = commandesTerminees.filter((c) => isRendezVous(c));
  const annuleesProduits = commandesAnnulees.filter((c) => !isRendezVous(c));
  const annuleesRdv = commandesAnnulees.filter((c) => isRendezVous(c));

  const productColSpan = 9;
  const rdvColSpan = 10;

  return (
    <div className="space-y-6">
      {showProductSections ? (
        <MesCommandesMaquetteBlock title="Commandes en cours" count={produitsEnCours.length} icon={Clock}>
          <div className="overflow-x-auto">
            <table className={MES_COMMANDES_MAQUETTE_TABLE_PLATFORM_CLASS}>
              <MesCommandesProductTableHead showCommercantColumn />
              <tbody className="bg-white">
                {produitsEnCours.length > 0 ? (
                  produitsEnCours.map((c) =>
                    renderPlatformRow(c, {
                      ctx: buildMesCommandesRowContext(c, rowContextDeps),
                      rowHandlers,
                      isNewCommande: newCommandes.includes(c.id),
                    }),
                  )
                ) : (
                  <MesCommandesMaquetteEmptyRow
                    colSpan={productColSpan}
                    message="Aucune commande produit en cours pour cette sélection."
                  />
                )}
              </tbody>
            </table>
          </div>
        </MesCommandesMaquetteBlock>
      ) : null}

      {showRdvSections ? (
        <MesCommandesMaquetteBlock
          title={MES_COMMANDES_PRESTATIONS_RDV_BLOCK_TITLE}
          showTitleCount={false}
          icon={Calendar}
        >
          <div className="overflow-x-auto">
            <table className={MES_COMMANDES_MAQUETTE_TABLE_PLATFORM_CLASS}>
              <MesCommandesRdvTableHead showCommercantColumn />
              <tbody className="bg-white">
                {rdvEnCours.length > 0 ? (
                  rdvEnCours.map((c) =>
                    renderPlatformRow(c, {
                      ctx: buildMesCommandesRowContext(c, rowContextDeps),
                      rowHandlers,
                      isNewCommande: newCommandes.includes(c.id),
                    }),
                  )
                ) : (
                  <MesCommandesMaquetteEmptyRow
                    colSpan={rdvColSpan}
                    message="Aucune prestation ou RDV planifié pour cette sélection."
                  />
                )}
              </tbody>
            </table>
          </div>
        </MesCommandesMaquetteBlock>
      ) : null}

      {commandesTerminees.length > 0 ? (
        <div className="bg-white rounded-lg shadow-lg border-2 border-green-200">
          <div
            className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-3 rounded-t-lg cursor-pointer hover:from-green-600 hover:to-green-700 transition-colors"
            onClick={() => onToggleSection('terminees')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onToggleSection('terminees');
            }}
            role="button"
            tabIndex={0}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-white mr-2" />
                <h2 className="text-lg font-bold text-white">
                  Commandes terminées ({commandesTerminees.length})
                </h2>
              </div>
              {sectionsExpanded.terminees ? (
                <ChevronUp className="h-5 w-5 text-white" />
              ) : (
                <ChevronDown className="h-5 w-5 text-white" />
              )}
            </div>
          </div>
          {sectionsExpanded.terminees ? (
            <div className="space-y-4 p-4">
              {termineesProduits.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className={MES_COMMANDES_MAQUETTE_TABLE_PLATFORM_CLASS}>
                    <MesCommandesProductTableHead showCommercantColumn />
                    <tbody className="bg-white">
                      {termineesProduits.map((c) =>
                        renderPlatformRow(c, {
                          ctx: buildMesCommandesRowContext(c, rowContextDeps),
                          rowHandlers,
                          dimmed: true,
                        }),
                      )}
                    </tbody>
                  </table>
                </div>
              ) : null}
              {termineesRdv.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className={MES_COMMANDES_MAQUETTE_TABLE_PLATFORM_CLASS}>
                    <MesCommandesRdvTableHead showCommercantColumn />
                    <tbody className="bg-white">
                      {termineesRdv.map((c) =>
                        renderPlatformRow(c, {
                          ctx: buildMesCommandesRowContext(c, rowContextDeps),
                          rowHandlers,
                          dimmed: true,
                        }),
                      )}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {commandesAnnulees.length > 0 ? (
        <div className="bg-white rounded-lg shadow-lg border-2 border-red-200">
          <div
            className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-3 rounded-t-lg cursor-pointer hover:from-red-600 hover:to-red-700 transition-colors"
            onClick={() => onToggleSection('annulees')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onToggleSection('annulees');
            }}
            role="button"
            tabIndex={0}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <XCircle className="h-5 w-5 text-white mr-2" />
                <h2 className="text-lg font-bold text-white">
                  Commandes annulées ({commandesAnnulees.length})
                </h2>
              </div>
              {sectionsExpanded.annulees ? (
                <ChevronUp className="h-5 w-5 text-white" />
              ) : (
                <ChevronDown className="h-5 w-5 text-white" />
              )}
            </div>
          </div>
          {sectionsExpanded.annulees ? (
            <div className="space-y-4 p-4">
              {annuleesProduits.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className={MES_COMMANDES_MAQUETTE_TABLE_PLATFORM_CLASS}>
                    <MesCommandesProductTableHead showCommercantColumn />
                    <tbody className="bg-white">
                      {annuleesProduits.map((c) =>
                        renderPlatformRow(c, {
                          ctx: buildMesCommandesRowContext(c, rowContextDeps),
                          rowHandlers,
                          dimmed: true,
                        }),
                      )}
                    </tbody>
                  </table>
                </div>
              ) : null}
              {annuleesRdv.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className={MES_COMMANDES_MAQUETTE_TABLE_PLATFORM_CLASS}>
                    <MesCommandesRdvTableHead showCommercantColumn />
                    <tbody className="bg-white">
                      {annuleesRdv.map((c) =>
                        renderPlatformRow(c, {
                          ctx: buildMesCommandesRowContext(c, rowContextDeps),
                          rowHandlers,
                          dimmed: true,
                        }),
                      )}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
